// ─── HU-073: Página Referência de Linha Editorial ─────────────────────────────
// Lê as linhas editoriais do cliente ativo via tRPC (tabela client_editorial_lines).
// Hoje hardcoded → agora 100% configurável por cliente.

import { useEditorialLines, type EditorialLine } from "@/hooks/useEditorialLines";

function Chips({ items, color, bg }: { items: string[]; color: string; bg: string }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <span key={i} className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: bg, color }}>
          {it}
        </span>
      ))}
    </div>
  );
}

export default function LinhaEditorial() {
  const { lines, isLoading } = useEditorialLines();

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--solar-bg)]">
        <p className="text-[var(--solar-mute)] text-sm">Carregando linhas editoriais…</p>
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--solar-bg)] p-6">
        <div className="max-w-md text-center bg-white rounded-xl border border-[var(--solar-line)] p-8 shadow-[var(--elev-1)]">
          <h2 className="font-bold text-base text-[var(--solar-ink)] mb-2" style={{ fontFamily: "Poppins, sans-serif" }}>
            Nenhuma linha editorial configurada
          </h2>
          <p className="text-sm text-[var(--ink-2)] mb-4">
            Este cliente ainda não tem linhas editoriais cadastradas. Um admin pode criá-las pelo painel de Configurações ou semeá-las a partir de um template.
          </p>
          <p className="text-xs text-[var(--solar-mute)]">
            Procure por <strong>Admin → Cliente → Linhas editoriais</strong>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[var(--solar-bg)]">
      <div className="bg-white border-b border-[var(--solar-line)] px-6 py-5 sticky top-0 z-10">
        <h1 className="font-bold text-xl text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
          Linha Editorial
        </h1>
        <p className="text-[var(--solar-mute)] text-xs mt-0.5">
          Referência estratégica · pilares, personas, formatos e mix semanal de cada linha
        </p>
      </div>

      <div className="px-6 py-6 max-w-5xl mx-auto">
        {/* Distribuição visual */}
        <div className="bg-white rounded-xl border border-[var(--solar-line)] p-5 mb-6 shadow-[var(--elev-1)]">
          <h2 className="font-bold text-sm text-[var(--solar-ink)] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
            Distribuição recomendada
          </h2>
          <div className="flex gap-1 h-8 rounded-xl overflow-hidden mb-3">
            {lines.map((l: EditorialLine) => (
              <div
                key={l.id}
                className="flex items-center justify-center text-[10px] font-bold text-white"
                style={{ background: l.color, flex: Math.max(l.frequenciaPct, 1) }}
                title={`${l.codigo} ${l.nome}: ${l.frequenciaPct}%`}
              >
                {l.codigo}
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            {lines.map((l: EditorialLine) => (
              <div key={l.id} className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
                <span className="text-xs text-[var(--ink-2)] font-medium">{l.codigo} {l.nome}</span>
                <span className="text-xs text-[var(--solar-mute)]">{l.frequenciaPct}% do calendário</span>
              </div>
            ))}
          </div>
        </div>

        {/* Cards individuais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {lines.map((l: EditorialLine) => (
            <div key={l.id} className="bg-white rounded-xl border border-[var(--solar-line)] overflow-hidden shadow-[var(--elev-1)] hover:shadow-[var(--elev-2)] transition-shadow">
              <div className="px-5 py-4 flex items-center gap-3" style={{ background: l.bg }}>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0"
                  style={{ background: l.color, fontFamily: "Poppins, sans-serif" }}
                >
                  {l.codigo}
                </div>
                <div>
                  <h3 className="font-bold text-sm" style={{ color: l.color, fontFamily: "Poppins, sans-serif" }}>
                    {l.nome}
                  </h3>
                  <p className="text-xs font-medium" style={{ color: l.color + "99" }}>
                    {l.frequenciaPct}% do calendário {l.tom ? `· ${l.tom}` : ""}
                  </p>
                </div>
              </div>

              <div className="px-5 py-4 space-y-4">
                {l.descricao && <p className="text-sm text-[var(--ink-2)] leading-relaxed">{l.descricao}</p>}

                {l.objetivo && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: l.color }}>Objetivo</p>
                    <p className="text-sm text-[var(--ink-2)]">{l.objetivo}</p>
                  </div>
                )}

                {l.pilares.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: l.color }}>Pilares</p>
                    <Chips items={l.pilares} color={l.color} bg={l.bg} />
                  </div>
                )}

                {l.formatos.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: l.color }}>Formatos recomendados</p>
                    <Chips items={l.formatos} color="var(--ink-2)" bg="var(--solar-bg)" />
                  </div>
                )}

                {l.personas.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: l.color }}>Personas</p>
                    <Chips items={l.personas} color="var(--brand)" bg="var(--brand-soft)" />
                  </div>
                )}

                {l.exemplos.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: l.color }}>Exemplos de ângulos</p>
                    <ul className="space-y-1.5">
                      {l.exemplos.map((ex: string, i: number) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: l.color }} />
                          <span className="text-sm text-[var(--ink-2)]">{ex}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {l.mixSemanal && (
                  <div className="flex items-center gap-2 pt-1" style={{ borderTop: "1px dashed var(--solar-line)" }}>
                    <span className="text-[10px] font-bold uppercase tracking-widest pt-2" style={{ color: l.color }}>Mix semanal</span>
                    <span className="text-sm font-semibold text-[var(--ink-2)] pt-2">{l.mixSemanal}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-[var(--solar-mute)] text-center mt-6">
          As proporções e o mix semanal são referências, ajuste pelo Admin conforme o contexto e objetivos do cliente
        </p>
      </div>
    </div>
  );
}
